// Apollo v4 error discrimination. v3's monolithic `ApolloError` class is
// removed in v4 and replaced by typed predicate classes:
// - `CombinedGraphQLErrors.is(err)` → response carried `errors[]`
// - `CombinedProtocolErrors.is(err)` → multipart-protocol-level errors
// - `LinkError` / `ServerError` / `ServerParseError` for transport-level
//
// These helpers keep call sites stable across v3→v4. The public surface
// (`isGraphQLError` / `getGraphQLErrors` / `getNetworkError` /
// `isNetworkError`) is unchanged from the v3 shim.
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  LocalStateError,
  UnconventionalError,
} from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

export type GraphQLErrorLike = GraphQLFormattedError;

export const isGraphQLError = (err: unknown): err is CombinedGraphQLErrors =>
  CombinedGraphQLErrors.is(err) && err.errors.length > 0;

// v3's `networkError` meant an error from the link chain — transport/protocol
// failures, including a bare `TypeError` from an offline fetch. It did NOT
// mean client-side Apollo errors. v4 splits errors into typed classes, and
// several client-side ones extend `Error`, so a naive `!isGraphQLError` would
// misclassify them as network. Exclude them explicitly:
//  - `CombinedGraphQLErrors` — carried in the GraphQL response payload.
//  - `LocalStateError` — an @client / local-resolver failure.
//  - `UnconventionalError` — a non-Error value was thrown.
// Cache `MissingFieldError` is absent by design: it has no public `.is()`, and
// the cache only attaches it to an internal diff result or logs it in dev, so
// it never reaches these helpers as an argument.
const isClientSideError = (err: unknown): boolean =>
  CombinedGraphQLErrors.is(err) ||
  LocalStateError.is(err) ||
  UnconventionalError.is(err);

export const isNetworkError = (err: unknown): err is Error =>
  CombinedProtocolErrors.is(err) ||
  (err instanceof Error && !isClientSideError(err));

export const getGraphQLErrors = (
  err: unknown,
): ReadonlyArray<GraphQLErrorLike> => {
  if (CombinedGraphQLErrors.is(err)) {
    return err.errors;
  }
  return [];
};

export const getNetworkError = (err: unknown): Error | null => {
  if (err instanceof Error && !isClientSideError(err)) {
    return err;
  }
  return null;
};

// A 401 from the GraphQL endpoint. `createApolloClient` answers it by calling
// `onUnauthenticated`, which redirects to login, so UI gating on a query's
// `error` must stay silent for it or it renders over the redirect.
export const isUnauthenticatedError = (err: unknown): boolean =>
  err instanceof Error &&
  'statusCode' in err &&
  (err as Error & { statusCode: unknown }).statusCode === 401;
